/**
 * Deterministic replacements for the banned `Math.*` functions (TRD-00 R-00-04).
 *
 * ECMAScript specifies `Math.sqrt` exactly (IEEE-754 requires correctly-rounded square
 * root) but leaves `sin`, `cos`, `atan2`, `pow`, `exp`, and `log` implementation-defined.
 * Two browsers — or two versions of one browser — may differ in the last bits. In a
 * simulation that must replay identically on a player's machine and on the server, that
 * is a silent leaderboard bug that would surface months later as "my score was rejected".
 *
 * Everything here is built from `+ - * /` and `Math.sqrt` only, so it is bit-identical
 * on every conforming engine. `Math.PI` is safe: the spec defines it as *the* Number
 * value nearest π, not as an approximation each engine chooses.
 *
 * ── Why polynomials and not a lookup table ──────────────────────────────────────────
 * TRD-01 R-01-05/R-01-06 originally specified a 4096-entry LUT with linear interpolation.
 * That was dropped during implementation for three reasons:
 *
 *   1. Accuracy. LUT + lerp over [0, 2π) has error ≈ h²/8 ≈ 2.9e-7. These series measure
 *      ~2e-14 against the native reference — seven orders of magnitude better, for the
 *      same cost class.
 *   2. Speed. A 4096-entry Float64Array is 32 KB, which does not stay in L1 under a
 *      wave-25 workload. Five fused multiply-adds beat a cache miss.
 *   3. Bootstrapping. R-01-05 required building the LUT *without* `Math.sin`, which
 *      means writing this series anyway — and then throwing away its precision.
 *
 * Measured cost is in `math.test.ts`. If it ever does become hot, the fix is caching at
 * the call site, not a table.
 *
 * ⚠ Any change to this file changes every replay. Bump SIM_VERSION (TRD-10 R-10-17).
 */

export const PI = Math.PI;
export const TAU = Math.PI * 2;
export const HALF_PI = Math.PI / 2;
export const QUARTER_PI = Math.PI / 4;

/** `Math.sqrt` is IEEE-754 correctly-rounded, so it is deterministic and re-exported as-is. */
export const sqrt = Math.sqrt;

// ── Trigonometry ──────────────────────────────────────────────────────────────────────

/**
 * Maclaurin series for sin on |x| ≤ π/4, in Horner form, through x¹³.
 *
 * Stopping at x¹¹ measured 5.3e-13 against the native reference — fine for gameplay, but
 * the x¹³ term costs one multiply-add and takes it to ~2e-14. Worth having: this is the
 * function every projectile angle and drone orbit routes through.
 */
function seriesSin(x: number): number {
  const x2 = x * x;
  return (
    x *
    (1 +
      x2 *
        (-1 / 6 +
          x2 *
            (1 / 120 +
              x2 * (-1 / 5040 + x2 * (1 / 362880 + x2 * (-1 / 39916800 + x2 * (1 / 6227020800)))))))
  );
}

/**
 * Maclaurin series for cos on |x| ≤ π/4, in Horner form, through x¹⁴.
 *
 * cos needs one more term than sin, which is not obvious: for equal polynomial degree its
 * next omitted term is one order lower. Stopping at x¹² put the worst error in the whole
 * library at exactly π/4 + k·π/2 — the switchover point where `sinQuarter` delegates to
 * this function with an argument near π/4, right where its truncation error peaks
 * ((π/4)¹⁴/14! ≈ 2.4e-13). The x¹⁴ term drops that to ~6e-16.
 */
function seriesCos(x: number): number {
  const x2 = x * x;
  return (
    1 +
    x2 *
      (-1 / 2 +
        x2 *
          (1 / 24 +
            x2 *
              (-1 / 720 +
                x2 *
                  (1 / 40320 +
                    x2 * (-1 / 3628800 + x2 * (1 / 479001600 + x2 * (-1 / 87178291200)))))))
  );
}

/** sin over a quarter turn, r ∈ [0, π/2]. Switching at π/4 keeps both series in range. */
function sinQuarter(r: number): number {
  return r <= QUARTER_PI ? seriesSin(r) : seriesCos(HALF_PI - r);
}

/** cos over a quarter turn, r ∈ [0, π/2]. */
function cosQuarter(r: number): number {
  return r <= QUARTER_PI ? seriesCos(r) : seriesSin(HALF_PI - r);
}

/** Wrap into [0, 2π). */
export function normalizeAngle(radians: number): number {
  const wrapped = radians - Math.floor(radians / TAU) * TAU;
  // Guard the boundary: for tiny negative inputs the division can round to exactly TAU.
  return wrapped >= TAU ? 0 : wrapped;
}

export function sin(radians: number): number {
  const a = normalizeAngle(radians);
  const quadrant = Math.floor(a / HALF_PI);
  const r = a - quadrant * HALF_PI;

  switch (quadrant) {
    case 0:
      return sinQuarter(r);
    case 1:
      return cosQuarter(r);
    case 2:
      return -sinQuarter(r);
    default:
      return -cosQuarter(r);
  }
}

export function cos(radians: number): number {
  const a = normalizeAngle(radians);
  const quadrant = Math.floor(a / HALF_PI);
  const r = a - quadrant * HALF_PI;

  switch (quadrant) {
    case 0:
      return cosQuarter(r);
    case 1:
      return -sinQuarter(r);
    case 2:
      return -cosQuarter(r);
    default:
      return sinQuarter(r);
  }
}

const SQRT3 = Math.sqrt(3);
/** tan(π/12) = 2 − √3. The reduction threshold below. */
const TAN_PI_12 = 2 - SQRT3;
const PI_OVER_6 = Math.PI / 6;

/**
 * atan on [0, 1].
 *
 * The naive Taylor series converges painfully slowly near x = 1 (error ~1/(2n+1)), so we
 * first apply the arctangent subtraction identity to fold [tan(π/12), 1] down into
 * [0, tan(π/12)]. On that reduced range the x¹³ truncation error is ≈ 0.268¹⁵/15 ≈ 1e-10.
 */
function atanUnit(x: number): number {
  if (x > TAN_PI_12) {
    return PI_OVER_6 + atanUnit((x * SQRT3 - 1) / (SQRT3 + x));
  }
  const x2 = x * x;
  return (
    x *
    (1 +
      x2 * (-1 / 3 + x2 * (1 / 5 + x2 * (-1 / 7 + x2 * (1 / 9 + x2 * (-1 / 11 + x2 * (1 / 13)))))))
  );
}

/** Full four-quadrant arctangent, result in (−π, π]. */
export function atan2(y: number, x: number): number {
  if (x === 0 && y === 0) return 0;

  const ax = x < 0 ? -x : x;
  const ay = y < 0 ? -y : y;

  const swapped = ay > ax;
  const ratio = swapped ? ax / ay : ay / ax;

  let angle = atanUnit(ratio);
  if (swapped) angle = HALF_PI - angle;
  if (x < 0) angle = PI - angle;
  return y < 0 ? -angle : angle;
}

// ── Geometry ──────────────────────────────────────────────────────────────────────────
//
// All positions are in TILE units, never pixels (TRD-01 R-01-10). Functions take flat
// x/y pairs rather than a Vec2 object: the hot loops run 400 enemies × 60 Hz and object
// allocation there is exactly the per-tick garbage TRD-01 R-01-12 warns about.

/** Squared distance. Prefer this — range checks compare against range², no sqrt needed. */
export function distSq(ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  return dx * dx + dy * dy;
}

export function dist(ax: number, ay: number, bx: number, by: number): number {
  return Math.sqrt(distSq(ax, ay, bx, by));
}

export function angleTo(fromX: number, fromY: number, toX: number, toY: number): number {
  return atan2(toY - fromY, toX - fromX);
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function clamp(value: number, lo: number, hi: number): number {
  return value < lo ? lo : value > hi ? hi : value;
}

export function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Shortest signed difference between two angles, in (−π, π]. */
export function angleDelta(from: number, to: number): number {
  let delta = normalizeAngle(to - from);
  if (delta > PI) delta -= TAU;
  return delta;
}

/** Rotate `from` toward `to` by at most `maxStep` radians — homing projectile steering. */
export function rotateToward(from: number, to: number, maxStep: number): number {
  const delta = angleDelta(from, to);
  if (delta > maxStep) return from + maxStep;
  if (delta < -maxStep) return from - maxStep;
  return to;
}
